/**
 * Builds sorted, de-duplicated client / project rows for header dropdowns.
 * When the user has clients, projects stay empty until a client is chosen (clear cascade).
 */

function sortByLabel(rows) {
  return [...rows].sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: 'base', numeric: true }),
  );
}

/** Resolve client id from project shapes used by auth profile / APIs. */
export function getProjectClientId(project) {
  if (!project || typeof project !== 'object') return '';
  const cid = project.client_id ?? project.clientId ?? project.client?.id ?? project.client?.client_id;
  return cid != null && cid !== '' ? String(cid) : '';
}

export function getProjectId(project) {
  if (!project || typeof project !== 'object') return '';
  const id = project.id ?? project.project_id ?? project.projectId;
  return id != null && id !== '' ? String(id) : '';
}

export function getSortedClientOptions(user) {
  const raw = user?.clients;
  if (!Array.isArray(raw) || raw.length === 0) return [];

  const byId = new Map();
  raw.forEach((c, i) => {
    const id = String(c.id ?? c.client_id ?? c.clientId ?? `c-${i}`);
    const label = String(c.name ?? c.client_name ?? c.label ?? `Client ${c.id ?? id}`).trim();
    if (!byId.has(id)) {
      byId.set(id, { id, label: label || `Client ${id}` });
    }
  });
  return sortByLabel([...byId.values()]);
}

export function getSortedProjectOptions(user, selectedClientId) {
  const raw = user?.projects;
  if (!Array.isArray(raw) || raw.length === 0) {
    // eslint-disable-next-line no-console
    console.log('[projects] empty: user.projects missing or empty', {
      selectedClientId,
      projectsType: typeof raw,
      projectsLength: Array.isArray(raw) ? raw.length : null,
    });
    return [];
  }

  const clients = user?.clients;
  const hasClients = Array.isArray(clients) && clients.length > 0;
  if (hasClients && !selectedClientId) {
    // eslint-disable-next-line no-console
    console.log('[projects] empty: client required but selectedClientId is blank', {
      hasClients,
      rawCount: raw.length,
    });
    return [];
  }

  let list = raw;
  if (selectedClientId) {
    list = raw.filter((p) => {
      const cid = getProjectClientId(p);
      // Projects without a client binding stay visible for any selected client.
      if (!cid) return true;
      return cid === String(selectedClientId);
    });
  }

  const byId = new Map();
  list.forEach((p, i) => {
    const id = getProjectId(p) || `p-${i}`;
    const label = String(p.name ?? p.project_name ?? p.label ?? `Project ${p.id ?? id}`).trim();
    if (!byId.has(id)) {
      byId.set(id, { id, label: label || `Project ${id}` });
    }
  });
  const result = sortByLabel([...byId.values()]);
  // eslint-disable-next-line no-console
  console.log('[projects] filtered options', {
    selectedClientId: selectedClientId != null ? String(selectedClientId) : null,
    rawCount: raw.length,
    filteredCount: list.length,
    optionCount: result.length,
    optionIds: result.map((p) => p.id),
  });
  return result;
}

export function clientListExists(user) {
  return Array.isArray(user?.clients) && user.clients.length > 0;
}

/**
 * Returns every project the user can access, ignoring any client-cascade gating.
 * Useful for assignment dialogs where the operator needs the full list at once.
 */
export function getAllProjectOptions(user) {
  const raw = user?.projects;
  if (!Array.isArray(raw) || raw.length === 0) return [];

  const byId = new Map();
  raw.forEach((p, i) => {
    const id = String(p.id ?? p.project_id ?? p.projectId ?? `p-${i}`);
    const label = String(p.name ?? p.project_name ?? p.label ?? `Project ${p.id ?? id}`).trim();
    if (!byId.has(id)) {
      byId.set(id, { id, label: label || `Project ${id}` });
    }
  });
  return sortByLabel([...byId.values()]);
}
